import { z } from 'zod'

/** FR-01: pflegbare Felder pro Showcase */
export const showcaseStatusSchema = z.enum(['draft', 'live'])
export type ShowcaseStatus = z.infer<typeof showcaseStatusSchema>

export const faqEntrySchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
})
export type FaqEntry = z.infer<typeof faqEntrySchema>

export const showcaseSchema = z.object({
  id: z.string(),
  slug: z.string().min(1),
  title: z.string().min(1),
  shortText: z.string().min(1),
  longText: z.string(),
  targetAudience: z.string().default(''),
  keyMessages: z.array(z.string()).default([]),
  /**
   * Weitere Einsatzbereiche, die J4F für den Showcase sieht — über den im Lab
   * gezeigten Fall hinaus (z. B. „Lebensmittelhandel: …“). K4ia nennt sie als
   * bestätigt; alles Weitere nur als gekennzeichnete Idee.
   */
  useCases: z.array(z.string()).default([]),
  faq: z.array(faqEntrySchema).default([]),
  /** FR-06: "Nicht sagen"-Regeln, z. B. keine Preise oder Kundennamen */
  tabooRules: z.array(z.string()).default([]),
  contactPerson: z.string().optional(),
  /**
   * FR-31/ADR 0003: visuelle Erkennungsmerkmale für die Showcase-Erkennung
   * per Kamera (Vision-LLM-Abfrage gegen den Katalog) — bewusst getrennt von
   * `shortText`, da diese Beschreibung den physischen Aufbau beschreibt
   * ("Tisch mit Kamera über einem Förderband"), nicht die Marketingbotschaft.
   */
  visualDescription: z.string().default(''),
  /**
   * „powered by": der Partner hinter dem Showcase. Leer heißt, es gibt keinen
   * Hinweis — bewusst eine Option, nicht jeder Showcase hat einen Partner.
   * Ist er gesetzt, steht unten „powered by <Partner>", sobald K4ia über den
   * Showcase erzählt (Intro-Bildschirm, Chat).
   */
  partnerName: z.string().max(80).default(''),
  /**
   * Logo des Partners, R2-Schlüssel unter `showcases/` (ausgeliefert über
   * `/media/<Schlüssel>`). Gesetzt nur über den eigenen Upload im Admin,
   * nie über das Formular. Ohne Logo steht der Name als Text.
   */
  partnerLogoKey: z.string().nullable().default(null),
  status: showcaseStatusSchema,
  sortOrder: z.number().int().default(0),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Showcase = z.infer<typeof showcaseSchema>

export const showcaseInputSchema = showcaseSchema.omit({
  id: true,
  partnerLogoKey: true,
  createdAt: true,
  updatedAt: true,
})
export type ShowcaseInput = z.infer<typeof showcaseInputSchema>

export const assetTypeSchema = z.enum(['image', 'video', 'pdf', 'reference_image'])
export type AssetType = z.infer<typeof assetTypeSchema>

export const assetSchema = z.object({
  id: z.string(),
  showcaseId: z.string(),
  type: assetTypeSchema,
  r2Key: z.string(),
  description: z.string().default(''),
  language: z.string().default('de'),
  /** viewpoint label for reference images, e.g. "front", "left-45deg" */
  angleLabel: z.string().optional(),
  createdAt: z.string(),
})
export type Asset = z.infer<typeof assetSchema>

export const documentStatusSchema = z.enum(['uploaded', 'indexing', 'indexed', 'error'])
export type DocumentStatus = z.infer<typeof documentStatusSchema>

export const documentSchema = z.object({
  id: z.string(),
  showcaseId: z.string().nullable(),
  title: z.string().min(1),
  source: z.string(),
  r2Key: z.string().nullable(),
  status: documentStatusSchema,
  version: z.number().int().default(1),
  errorMessage: z.string().optional(),
  /** Thema für Wissen ohne Showcase (KNOWLEDGE_TOPICS); null bei Showcase-Dokumenten. */
  topic: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Document = z.infer<typeof documentSchema>

export const chunkSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  text: z.string().min(1),
  position: z.number().int(),
  vectorId: z.string().nullable(),
})
export type Chunk = z.infer<typeof chunkSchema>

export const nfcTargetSchema = z.enum(['start', 'showcase'])
export type NfcTarget = z.infer<typeof nfcTargetSchema>

export const nfcTagSchema = z.object({
  id: z.string(),
  tagUid: z.string(),
  target: nfcTargetSchema,
  showcaseId: z.string().nullable(),
  location: z.string().optional(),
  createdAt: z.string(),
})
export type NfcTag = z.infer<typeof nfcTagSchema>
